/* eslint-disable camelcase */
import axios, { AxiosResponse } from 'axios'
import { IDadosPessoais } from 'src/components/pagamento/formDadosPessoais'
import config from 'src/config'
import api from 'src/services/api'
import { ItemGenerico } from '../dashboard'
import { ObjetoDescricaoGenerico } from 'src/types'
import { IPagamentoAvulsoRequest } from 'src/contexts/Pagamento/context'
import {
  resumoPagamentoMockEtapasV21,
  resumoPagamentoMockEtapasV212,
  resumoPagamentoMockEtapasV2,
  resumoPagamentoMockEtapasV1
} from './mockResumoEtapas'

export interface Paciente {
  nome: string
  cpf: string
  idade: string
}

export interface Pagamento {
  id: string
  status: string
  valor: number
  juros: number
  tipo: string
  integral: boolean

  criado: string
  criadaPor: string
  modificado: string
  modificadoPor: string
  urlBoleto: string
  urlQrCode: string
  codigoBoleto: string
  pixCopiaCola: string

  codigoTransacao: string
  codigoEstabelecimento: string
  dataPagamento: string
  formaPagamento: string
  meioPagamento: string
  tipoPagamento: string
  urlComprovante: string
  motivoRecusa: string
  registroManual: boolean
}

export interface PagamentoResumoResponse {
  codigoProposta: string
  statusProposta: string
  procedimento: string
  pago: boolean
  medico: string
  localAtendimento: string
  paciente: Paciente
  valorTotal: number
  valorPago: number
  valorEmProcessamento: number
  saldoDevedor: number
  etapas: Etapa[]
  etapaUnica: Etapa
  parcelamento: Parcelamento
  dadosPessoais: IDadosPessoais
  etapaAtual: number
  existeEtapasDB: boolean
  existeEtapaProvisoria: boolean
  versaoPagamento: 'v1' | 'v2' | 'v2.1'
  itensContemplados: string
  aceitaSeguro?: boolean
  foraDoCorteSeguro?: boolean
  pagamentoIntegral?: boolean
  linkGoogleFormsSeguro?: string
  dadosRecebedorIncompleto?: boolean
  mensagemDadosRecebedorIncompleto?: string
}

export interface Etapa {
  titulo: string
  descricao: string
  parceiros: ParceiroEtapaPagamento[]
  pedidos: Pagamento[]
  valor: number
  saldoDevedor: number
  status: StatusEtapaEnum
  statusEtapa: StatusEtapaPagamentoEnum
  valorEmProcessamento: number
  valorPago: number
}

export interface ParceiroEtapaPagamento {
  nome: string
  valor: number
  recebedorId: string
  tipoRecebedor: string
}
export enum StatusEtapaEnum {
  Pendente = 'Pendente',
  Pago = 'Pago',
  EmAndamento = 'Em andamento'
}
export enum StatusEtapaPagamentoEnum {
  VIRTUAL = 1,
  PROVISORIO = 2,
  DEFINITIVO = 3
}

const STATUS_ETAPA_PRIORIDADE = [
  StatusEtapaPagamentoEnum.DEFINITIVO,
  StatusEtapaPagamentoEnum.PROVISORIO,
  StatusEtapaPagamentoEnum.VIRTUAL
]

export function etapaEstaPersistida(
  statusEtapa?: StatusEtapaPagamentoEnum | null
): boolean {
  return (
    statusEtapa === StatusEtapaPagamentoEnum.PROVISORIO ||
    statusEtapa === StatusEtapaPagamentoEnum.DEFINITIVO
  )
}

export function obterEtapasParaExibir(
  resumo?: Pick<PagamentoResumoResponse, 'etapas' | 'etapaUnica'> | null
): Etapa[] {
  let resultado: Etapa[] = resumo?.etapas ?? []

  for (const status of STATUS_ETAPA_PRIORIDADE) {
    if (resumo?.etapas?.some(e => e.statusEtapa === status)) {
      resultado = resumo.etapas
      break
    }

    if (resumo?.etapaUnica?.statusEtapa === status) {
      resultado = [resumo.etapaUnica]
      break
    }
  }

  return resultado
}

export function ehEtapaUnicaParaExibir(
  resumo?: Pick<PagamentoResumoResponse, 'etapas' | 'etapaUnica'> | null
): boolean {
  const etapas = obterEtapasParaExibir(resumo)
  return Boolean(
    resumo?.etapaUnica &&
      etapas.length === 1 &&
      etapas[0] === resumo.etapaUnica
  )
}

export interface InstrucoesNotaFiscalResponse {
  contatoParceiros: ContatoParceiro[]
}

export interface ContatoParceiro {
  descricao: string
  telefone: string
  email: string
}

export interface Parcelamento {
  valorSemJuros: number
  parcelas: Parcelas[]
}

export interface Parcelas {
  qtdeParcelas: number
  valor: number
  valorTotal: number
  juros: number
  parceirosAssumiramJuros: string[]
}

export const obterResumoPorPropostaId: (
  id: string
) => Promise<AxiosResponse<PagamentoResumoResponse>> = async id => {
  if (id === 'mockv212') {
    return {
      data: resumoPagamentoMockEtapasV212
    } as AxiosResponse<PagamentoResumoResponse>
  }
  if (id === 'mockv21') {
    return {
      data: resumoPagamentoMockEtapasV21
    } as AxiosResponse<PagamentoResumoResponse>
  }
  if (id === 'mockv2') {
    return {
      data: resumoPagamentoMockEtapasV2
    } as AxiosResponse<PagamentoResumoResponse>
  }
  if (id === 'mockv1') {
    return {
      data: resumoPagamentoMockEtapasV1
    } as AxiosResponse<PagamentoResumoResponse>
  }

  return api.get(`/pagamento/proposta/${id}`)
}

export const obterInstrucoesNFPorPropostaId: (
  id: string
) => Promise<AxiosResponse<InstrucoesNotaFiscalResponse>> = id => {
  return api.get(`/pagamento/proposta/${id}/instrucoes`)
}

export const calcularParcelas: (
  valor: number,
  propostaId: string,
  etapa?: number
) => Promise<AxiosResponse<Parcelamento>> = (valor, propostaId, etapa) => {
  return api.post('/pagamento/obterParcelas', { valor, propostaId, etapa })
}

export interface DestinatarioComprovanteResponse {
  tipo: number
  nome: string
  valor: string
  id: string
}

export const obterRecebedoresComprovantes: (
  id: string
) => Promise<AxiosResponse<DestinatarioComprovanteResponse[]>> = id => {
  return api.get(`/pagamento/proposta/${id}/recebedores`)
}
export interface PagamentoPixRequest {
  propostaId: string
  formaPagamento?: string
  valor?: number
  integral: boolean
  etapa?: number

  cidade: string
  cep: string
  endereco: string
  estado: string
  bairro: string
  numero: string

  documento: string
  email: string
  nome: string
  telefone: string
}

export interface PagamentoPixResponse {
  urlPix: string
  codigoPix: string
}

export const realizarPagamentoPix: (
  request: PagamentoPixRequest
) => Promise<AxiosResponse<PagamentoPixResponse>> = request => {
  var body: any = request
  body.valor = request.valor?.toFixed(2)
  body.formaPagamento = 'pix'
  return api.post('/pagamento/pix', body)
}

export interface PagamentoBoletoRequest {
  propostaId: string
  formaPagamento?: string
  valor?: number
  integral: boolean
  etapa?: number
  cidade: string
  cep: string
  endereco: string
  estado: string
  bairro: string
  numero: string

  documento: string
  email: string
  nome: string
  telefone: string
}

export interface PagamentoBoletoResponse {
  urlBoleto: string
  barcode: string
  codigoBarra: string
  pdfBoleto: string
}

export const realizarPagamentoBoleto: (
  request: PagamentoBoletoRequest
) => Promise<AxiosResponse<PagamentoBoletoResponse>> = request => {
  var body: any = request
  body.valor = request.valor?.toFixed(2)
  body.formaPagamento = 'boleto'

  return api.post('/pagamento/boleto', body)
}

export interface PagamentoCartaoCreditoRequest {
  propostaId: string
  formaPagamento?: string
  valor?: number
  integral: boolean
  etapa?: number

  tokenCartao: string
  numeroParcela: number
  cidade: string
  cep: string
  endereco: string
  estado: string
  bairro: string
  numero: string

  documento: string
  email: string
  nome: string
  telefone: string
}

export interface PagamentoCartaoCreditoResponse {
  fluxoAntifraude: any
  pagamentoCompleto: boolean
}

export const realizarPagamentoCartaoCredito: (
  request: PagamentoCartaoCreditoRequest
) => Promise<AxiosResponse<PagamentoCartaoCreditoResponse>> = request => {
  var body: any = request

  body.valor = request.valor?.toFixed(2)
  body.formaPagamento = 'credito'
  return api.post('/pagamento/cartaocredito', body)
}

export interface PagarmeTokenRequest {
  holderDocument: string
  expMonth: number
  expYear: number
  number: string
  holderName: string
  cvv: string
}

export interface PagarmeTokenResponse {
  id: string
  type: string
  created_at: string
  expires_at: string
}

export const criarTokenPagarme: (
  request: PagarmeTokenRequest
) => Promise<AxiosResponse<PagarmeTokenResponse>> = request => {
  const body = {
    card: {
      holder_document: request.holderDocument.trim(),
      exp_month: request.expMonth,
      exp_year: request.expYear,
      number: request.number.trim(),
      holder_name: request.holderName.trim(),
      cvv: request.cvv.trim()
    },
    type: 'card'
  }

  // const options = {
  //   method: 'POST',
  //   url: 'https://api.pagar.me/core/v5/tokens',
  //   params: { appId: config.PublicKeyPagarme },
  //   headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  //   data: body
  // }
  return axios.request({
    method: 'POST',
    url: 'https://api.pagar.me/core/v5/tokens',
    params: { appId: config.PublicKeyPagarme },
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    data: body
  })
  // return axios.request<PagarmeTokenRequest, PagarmeTokenResponse>(options)
  // return api.post(`/tokens?appId=${config.PublicKeyPagarme}`, body, {
  //   baseURL: 'https://api.pagar.me/core/v5'
  // })
}

export const salvarDadosPessoaisPagador: (
  request: IDadosPessoais
) => Promise<AxiosResponse<any>> = request => {
  var body: any = request
  if (
    request.propostaId === 'mockv212' ||
    request.propostaId === 'mockv21' ||
    request.propostaId === 'mockv2' ||
    request.propostaId === 'mockv1'
  ) {
    return new Promise(resolve => {
      resolve({
        data: {
          id: '1',
          nome: 'John Doe',
          email: 'john.doe@example.com',
          telefone: '1234567890'
        }
      } as AxiosResponse<any>)
    })
  }

  return api.post('/dadospagador/salvar', body)
}

export interface PagamentoAvulsoResponse {
  id?: string
  dataPagamento: string
  valor: number
  juros: number
  formaPagamento: string
  tipoPagamento: string
  codigoEstabelecimento: string
  urlComprovante: string
}

export const realizarPagamentoAvulso: (
  request: IPagamentoAvulsoRequest
) => Promise<AxiosResponse<PagamentoAvulsoResponse>> = request => {
  const fromData = new FormData()

  for (var key in request) {
    if (request[key] !== undefined && request[key] !== null) {
      fromData.append(key, request[key])
    }
  }

  return api.post('/pagamento/RegistrarPagamentoManual', fromData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
}

export const excluirPagamentoAvulso: (
  pedidoId: string
) => Promise<AxiosResponse<void>> = pedidoId => {
  return api.delete(`/pagamento/${pedidoId}`)
}

export const confirmarPagamentoManual: (
  pedidoId: string
) => Promise<AxiosResponse<void>> = pedidoId => {
  return api.post('/pagamento/ConfirmarPagamentoManual', { pedidoId })
}

export const recusarPagamentoManual: (
  pedidoId: string,
  motivoRecusa: string
) => Promise<AxiosResponse<void>> = (pedidoId, motivoRecusa) => {
  return api.post('/pagamento/RecusarPagamentoManual', {
    pedidoId,
    motivoRecusa
  })
}

export const registrarCliqueSeguro: (
  propostaId: string
) => Promise<AxiosResponse<void>> = propostaId => {
  return api.post(`/pagamento/proposta/${propostaId}/clique-seguro`)
}
